import type { Video } from "agir-types";
import type { Videos } from "next/dist/lib/metadata/types/metadata-types";
import type { ReactNode } from "react";
import type { VideoObject, WithContext } from "schema-dts";

import type { CookieService } from "@/src/infrastructure/cookieconsent/CookieService";

import { iframeStyle } from "@/src/components/ui/iframeStyle";
import { CookieCategory } from "@/src/infrastructure/cookieconsent/CookieCategory";

const style = iframeStyle(false, { height: "100%", width: "100%" });

export const youTubeThumbnailOrigin = "https://img.youtube.com";
export const youTubeEmbedOrigin = "https://www.youtube-nocookie.com";

function youTubeEmbedUrl(youTubeId: string): string {
  return `${youTubeEmbedOrigin}/embed/${youTubeId}`;
}

function youTubeThumbnailUrl(youTubeId: string): string {
  return `${youTubeThumbnailOrigin}/vi_webp/${youTubeId}/hqdefault.webp`;
}

export const youTubeCookie: CookieService<CookieCategory.FEATURES> = {
  category: CookieCategory.FEATURES,
  iframe: {
    embedUrl: youTubeEmbedUrl("{data-id}"),
    iframe: {
      allow:
        "accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen;",
    },
    label: "Pour regarder la vidéo",
    thumbnailUrl: youTubeThumbnailUrl("{data-id}"),
  },
  name: "YouTube",
  privacyPolicyUrl: "https://www.youtube.com/howyoutubeworks/privacy/",
  purpose: "Vidéos",
};

interface YouTubeProps {
  readonly id: string;
  readonly priority?: boolean;
}

export function youTubeSchema(
  { date, youTubeId }: Video,
  name: string,
  description: string = name,
): WithContext<VideoObject> {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    description,
    embedUrl: youTubeEmbedUrl(youTubeId),
    name,
    thumbnailUrl: [youTubeThumbnailUrl(youTubeId)],
    uploadDate: date,
  };
}

export function youTubeSitemap(
  { date, youTubeId }: Video,
  title: string,
  description: null | string,
): Videos {
  return {
    description: description ?? "",
    player_loc: youTubeEmbedUrl(youTubeId),
    publication_date: date,
    thumbnail_loc: youTubeThumbnailUrl(youTubeId),
    title,
  };
}

export function YouTubeVideo({ id, priority = true }: YouTubeProps): ReactNode {
  return (
    <div
      data-autoscale
      data-id={id}
      data-iframe-fetchpriority={priority ? "high" : "low"}
      data-service={youTubeCookie.name}
      style={style}
    />
  );
}
